import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../store";

export const selectUIState = (state: RootState) => state.ui;

export const selectSidebarOpen = (state: RootState) => state.ui.sidebarOpen;
export const selectTheme = (state: RootState) => state.ui.theme;
export const selectLanguage = (state: RootState) => state.ui.language;
export const selectNotifications = (state: RootState) => state.ui.notifications;
export const selectModals = (state: RootState) => state.ui.modals;
export const selectLoading = (state: RootState) => state.ui.loading;
export const selectBreadcrumbs = (state: RootState) => state.ui.breadcrumbs;

export const selectUnreadNotifications = createSelector(
  [selectNotifications],
  (notifications) => notifications.filter((n) => !n.read)
);

export const selectUnreadNotificationsCount = createSelector(
  [selectUnreadNotifications],
  (notifications) => notifications.length
);

export const selectNotificationsByType = createSelector(
  [selectNotifications],
  (notifications) => {
    return notifications.reduce((acc, notification) => {
      acc[notification.type] = (acc[notification.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }
);

export const selectRecentNotifications = createSelector(
  [selectNotifications],
  (notifications) => {
    return [...notifications]
      .sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      )
      .slice(0, 5);
  }
);

export const selectModalState = (modalName: string) =>
  createSelector(
    [selectModals],
    (modals) => modals[modalName] || { open: false, data: undefined }
  );

export const selectIsModalOpen = (modalName: string) =>
  createSelector([selectModalState(modalName)], (modal) => modal.open);

export const selectModalData = (modalName: string) =>
  createSelector([selectModalState(modalName)], (modal) => modal.data);

export const selectIsLoading = (key: string) =>
  createSelector([selectLoading], (loading) => loading[key] || false);

export const selectAnyLoading = createSelector([selectLoading], (loading) =>
  Object.values(loading).some((isLoading) => isLoading)
);

export const selectLoadingKeys = createSelector([selectLoading], (loading) =>
  Object.keys(loading).filter((key) => loading[key])
);

export const selectBreadcrumbsCount = createSelector(
  [selectBreadcrumbs],
  (breadcrumbs) => breadcrumbs.length
);

export const selectLastBreadcrumb = createSelector(
  [selectBreadcrumbs],
  (breadcrumbs) => breadcrumbs[breadcrumbs.length - 1] || null
);

export const selectIsDarkTheme = createSelector(
  [selectTheme],
  (theme) => theme === "dark"
);

export const selectIsLightTheme = createSelector(
  [selectTheme],
  (theme) => theme === "light"
);

export const selectUIStatus = createSelector(
  [
    selectSidebarOpen,
    selectTheme,
    selectLanguage,
    selectUnreadNotificationsCount,
  ],
  (sidebarOpen, theme, language, unreadCount) => ({
    sidebarOpen,
    theme,
    language,
    unreadNotifications: unreadCount,
  })
);
